import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SchoolLayout from '../../components/SchoolLayout';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ArrowLeft, User, Phone, MapPin, Calendar, CreditCard, Edit } from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  'Active': 'bg-green-100 text-green-800 border-green-200',
  'Inactive': 'bg-gray-100 text-gray-800 border-gray-200',
  'Suspended': 'bg-red-100 text-red-800 border-red-200',
  'Deceased': 'bg-black/10 text-black border-black/20',
  'Transferred': 'bg-blue-100 text-blue-800 border-blue-200',
};

const MemberProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMember();
  }, [id]);

  const fetchMember = async () => {
    try {
      const response = await axios.get(`${API_URL}/members/${id}`);
      setMember(response.data);
    } catch (error) {
      console.error('Failed to fetch member:', error);
      toast.error('Gagal memuatkan maklumat ahli');
    } finally {
      setLoading(false);
    }
  };

  const verifyUrl = `${window.location.origin}/verify?token=${member?.qr_token}`;

  if (loading) {
    return (
      <SchoolLayout>
        <div className="text-center py-12">
          <div className="animate-pulse text-[#D71920]">Memuatkan...</div>
        </div>
      </SchoolLayout>
    );
  }

  if (!member) {
    return (
      <SchoolLayout>
        <div className="text-center py-12">
          <p className="text-[#777]">Ahli tidak dijumpai</p>
        </div>
      </SchoolLayout>
    );
  }

  return (
    <SchoolLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => navigate('/school/members')}
            className="border-[#E5E5E5]"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Kembali
          </Button>
          
          <Button
            onClick={() => navigate(`/school/members/${id}/edit`)}
            className="bg-[#D71920] hover:bg-[#b8161c] text-white"
            data-testid="edit-button"
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Button>
        </div>

        {/* Header */}
        <div className="bg-white rounded-lg border border-[#E5E5E5] p-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Profile Photo Placeholder */}
            <div className="flex-shrink-0">
              <div className="w-32 h-32 rounded-full bg-[#D71920]/10 flex items-center justify-center border-4 border-[#D4AF37]">
                <User className="w-16 h-16 text-[#D71920]" />
              </div>
            </div>
            
            {/* Info */}
            <div className="flex-1">
              <div className="flex items-center flex-wrap gap-3 mb-2">
                <h1 className="text-3xl font-bold text-[#D71920]" >
                  {member.full_name}
                </h1>
                <span className={`px-3 py-1 rounded text-sm font-medium border ${statusColors[member.status]}`}>
                  {member.status}
                </span>
              </div>
              <p className="text-lg font-mono text-[#D71920] font-semibold">{member.member_no}</p>
            </div>

            {/* QR Code */}
            <div className="flex-shrink-0 text-center">
              <div className="bg-white p-4 rounded-lg border border-[#E5E5E5] shadow-sm">
                <QRCodeSVG value={verifyUrl} size={120} />
                <p className="text-xs text-[#777] mt-2">Kod QR Pengesahan</p>
              </div>
            </div>
          </div>
        </div>

        <div ></div>

        {/* Details Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Personal Info */}
          <div className="bg-white rounded-lg border border-[#E5E5E5] p-6">
            <h3 className="text-xl font-semibold text-[#1A1A1A] mb-4" >
              Maklumat Peribadi
            </h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <CreditCard className="w-5 h-5 text-[#D71920]" />
                <div>
                  <p className="text-sm text-[#777]">No. Kad Pengenalan</p>
                  <p className="font-medium text-[#1A1A1A] font-mono">{member.ic_masked}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Calendar className="w-5 h-5 text-[#D71920]" />
                <div>
                  <p className="text-sm text-[#777]">Tarikh Lahir</p>
                  <p className="font-medium text-[#1A1A1A]">{member.dob}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <User className="w-5 h-5 text-[#D71920]" />
                <div>
                  <p className="text-sm text-[#777]">Jantina</p>
                  <p className="font-medium text-[#1A1A1A]">{member.gender}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="bg-white rounded-lg border border-[#E5E5E5] p-6">
            <h3 className="text-xl font-semibold text-[#1A1A1A] mb-4" >
              Maklumat Hubungan
            </h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-[#D71920]" />
                <div>
                  <p className="text-sm text-[#777]">No. Telefon</p>
                  <p className="font-medium text-[#1A1A1A]">{member.phone}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-[#D71920] mt-1" />
                <div>
                  <p className="text-sm text-[#777]">Alamat</p>
                  <p className="font-medium text-[#1A1A1A]">{member.address}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Registration Info */}
          <div className="bg-white rounded-lg border border-[#E5E5E5] p-6 lg:col-span-2">
            <h3 className="text-xl font-semibold text-[#1A1A1A] mb-4" >
              Maklumat Pendaftaran
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-[#777]">Tarikh Didaftar</p>
                <p className="font-medium text-[#1A1A1A]">
                  {new Date(member.created_at).toLocaleDateString('ms-MY', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
              {member.approved_at && (
                <div>
                  <p className="text-sm text-[#777]">Tarikh Diluluskan</p>
                  <p className="font-medium text-[#1A1A1A]">
                    {new Date(member.approved_at).toLocaleDateString('ms-MY', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              )}
              {member.approval_remark && (
                <div>
                  <p className="text-sm text-[#777]">Catatan</p>
                  <p className="font-medium text-[#1A1A1A]">{member.approval_remark}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </SchoolLayout>
  );
};

export default MemberProfile;
